import { linkedInWriteScopeVerdict } from '@/lib/paid-media/linkedin/write-scope';
import { getPlatformLabel } from '@/lib/platforms/identity';
import type { PlatformType } from '@/services/socialAuth/types';
import { getPlatformIcon as getCanonicalPlatformIcon } from '@/utils/platformUtils';

export type IntegrationPlatformKey = PlatformType | 'google-drive';

/**
 * A connection-level error that means the stored OAuth token is no longer usable
 * — e.g. the vault secret was orphaned by the data-center cutover, the provider
 * revoked it, or it expired. The backend health probe writes `token_invalid`.
 * These surface as the amber "Reconnect" affordance (a one-click re-auth fixes
 * it), never the hard red "Error" state which is reserved for genuine faults.
 */
export const isReconnectError = (error: string | null | undefined): boolean => {
  if (!error) return false;
  const e = error.toLowerCase();
  return (
    e === 'token_invalid' ||
    e.includes('token') ||
    e.includes('credential') ||
    e.includes('reconnect') ||
    e.includes('expired') ||
    e.includes('unauthorized') ||
    e.includes('publish_actions') ||
    e.includes('permission')
  );
};

/**
 * Whether a connection has any stored OAuth vault pointer. The token id can live
 * either on the `access_token_vault_id` COLUMN or inside `metadata` (legacy
 * connections) — checking only metadata falsely marks column-stored connections
 * (e.g. Instagram) as "missing credentials".
 */
/**
 * True when the connection holds a refresh token the backend can redeem on its
 * own (refresh-platform-tokens cron + on-demand refresh in the token manager).
 * For these connections the access-token TTL is an implementation detail —
 * Google's is 1 hour by design — so expiry must never be surfaced to the user.
 */
export const hasRefreshTokenPointer = (rawData: unknown): boolean => {
  const r = (rawData ?? {}) as {
    refresh_token_vault_id?: string | null;
    metadata?: Record<string, unknown> | null;
  };
  const meta = r.metadata ?? {};
  return !!r.refresh_token_vault_id || !!meta.refresh_token_vault_id;
};

export const hasVaultPointer = (rawData: unknown): boolean => {
  const r = (rawData ?? {}) as {
    access_token_vault_id?: string | null;
    metadata?: Record<string, unknown> | null;
  };
  const meta = r.metadata ?? {};
  return (
    !!r.access_token_vault_id ||
    !!meta.access_token_vault_id ||
    !!meta.vault_id ||
    !!meta.page_access_token_vault_id
  );
};

/**
 * Map platform IDs to display names
 */
export const getPlatformDisplayName = (platformKey: IntegrationPlatformKey | string): string => {
  return getPlatformLabel(platformKey);
};

/**
 * Get the category for a platform
 */
export const getPlatformCategory = (
  platformKey: PlatformType,
): 'ecommerce' | 'social' | 'analytics' | 'other' => {
  switch (platformKey) {
    case 'shopify':
      return 'ecommerce';
    case 'facebook':
    case 'instagram':
    case 'linkedin':
    case 'tiktok':
    case 'pinterest':
      return 'social';
    case 'youtube':
    case 'google-analytics':
      return 'analytics';
    case 'google-business':
    case 'meta-ads':
    case 'google-ads':
    case 'linkedin-ads':
    case 'slack':
      return 'other';
    default: {
      // Wrap default case in braces
      // This should now be unreachable if PlatformType is exhaustive

      const _exhaustiveCheck: never = platformKey;
      return 'other';
    }
  }
};

/**
 * Get profile URL based on the platform and account info
 */
export const getProfileUrl = (
  platformKey: PlatformType,
  accountInfo?: Record<string, unknown>, // Use Record<string, unknown>
): string | null => {
  if (!accountInfo) return null;

  switch (platformKey) {
    case 'instagram':
      return typeof accountInfo.profile_url === 'string' ? accountInfo.profile_url : null;
    case 'facebook':
      return typeof accountInfo.page_url === 'string' ? accountInfo.page_url : null;
    case 'linkedin':
      return typeof accountInfo.profile_url === 'string' ? accountInfo.profile_url : null;
    case 'shopify': {
      const shopDomain =
        typeof accountInfo.shop_domain === 'string' ? accountInfo.shop_domain : null;
      return shopDomain ? `https://${shopDomain}` : null;
    }
    case 'youtube': {
      const channelId = typeof accountInfo.channel_id === 'string' ? accountInfo.channel_id : null;
      const platformUserId =
        typeof accountInfo.platform_user_id === 'string' ? accountInfo.platform_user_id : null;
      const id = channelId || platformUserId;
      return id ? `https://www.youtube.com/channel/${id}` : null;
    }
    default:
      return null;
  }
};

/**
 * Get profile image URL based on the platform and account info
 */
export const getProfileImage = (
  platformKey: PlatformType,
  accountInfo?: Record<string, unknown>, // Use Record<string, unknown>
): string | null => {
  if (!accountInfo) return null;

  switch (platformKey) {
    case 'instagram':
      return typeof accountInfo.profile_picture_url === 'string'
        ? accountInfo.profile_picture_url
        : null;
    case 'facebook':
      return typeof accountInfo.profile_picture_url === 'string'
        ? accountInfo.profile_picture_url
        : null;
    case 'linkedin':
      return typeof accountInfo.profile_picture_url === 'string'
        ? accountInfo.profile_picture_url
        : null;
    case 'shopify':
      return typeof accountInfo.logo_url === 'string' ? accountInfo.logo_url : null;
    case 'youtube':
      return typeof accountInfo.profile_image_url === 'string'
        ? accountInfo.profile_image_url
        : null;
    default:
      return null;
  }
};

/**
 * Get the appropriate icon component for a platform
 */
export const getPlatformIcon = (platformKey: PlatformType | string) =>
  getCanonicalPlatformIcon(platformKey);

/**
 * Move a single high-intent platform to the front of the "Available" list
 * without disturbing the relative order of the rest.
 *
 * Used by onboarding: a merchant who just selected "E-commerce / Shopify"
 * should see Shopify first, not buried behind Facebook/Pinterest in the global
 * config order. Pure + non-mutating so the Settings page (which passes no
 * priority) keeps the canonical order untouched.
 */
export function prioritizeAvailablePlatforms<T>(
  platforms: [string, T][],
  priorityPlatform?: string | null,
): [string, T][] {
  if (!priorityPlatform) return platforms;
  const idx = platforms.findIndex(([platform]) => platform === priorityPlatform);
  if (idx <= 0) return platforms;
  const next = [...platforms];
  const [entry] = next.splice(idx, 1);
  next.unshift(entry);
  return next;
}

export const LINKEDIN_ADS_WRITE_SCOPE_WARNING =
  "Can read reporting, but can't create campaigns. Reauthorize LinkedIn Ads and approve campaign management.";

/**
 * Additive warning for a LinkedIn Ads connection whose recorded OAuth scopes lack
 * `rw_ads`. The connection still reads, so its "Connected" state is unchanged.
 * An unknown scope list yields no warning (the provider stays the judge).
 */
export function linkedInAdsWriteScopeWarning(platform: string, rawData: unknown): string | null {
  if (platform !== 'linkedin-ads') return null;
  const raw = rawData as {
    granted_scopes?: unknown;
    connection_data?: { scopes?: unknown } | null;
  } | null;
  const verdict = linkedInWriteScopeVerdict({
    grantedScopes: raw?.granted_scopes,
    connectionDataScopes: raw?.connection_data?.scopes,
  });
  return verdict.ok ? null : LINKEDIN_ADS_WRITE_SCOPE_WARNING;
}
